<template>
  <div v-if="attachmentsPage" class="page-group">
    <div class="items">
      {{
        t('general__text__showing') +
        ' ' +
        attachmentsPage.numberOfElements +
        ' ' +
        t('general__text__of').toLocaleLowerCase() +
        ' ' +
        attachmentsPage.totalElements +
        ' ' +
        t('general__label__files').toLocaleLowerCase()
      }}
    </div>
    <div class="pagination">
      <PaginationBack :disabled="activePage === 1" @onClicked="handleClickBack" />
      <div v-if="attachmentsPage.totalPages <= 5" class="pagination">
        <PaginationElement
          v-for="pageNumber in attachmentsPage?.totalPages"
          :key="pageNumber"
          :page="pageNumber"
          :isActive="activePage"
          @onClicked="setActivePage"
        />
      </div>
      <div v-else-if="lastPages.includes(activePage)" class="pagination">
        <PaginationElement
          v-for="pageNumber in [
            attachmentsPage.totalPages - 4,
            attachmentsPage.totalPages - 3,
            attachmentsPage.totalPages - 2,
            attachmentsPage.totalPages - 1,
            attachmentsPage.totalPages
          ]"
          :key="pageNumber"
          :page="pageNumber"
          :isActive="activePage"
          @onClicked="setActivePage"
        />
      </div>
      <div v-else class="pagination">
        <PaginationElement
          v-for="pageNumber in [activePage, activePage + 1]"
          :key="pageNumber"
          :page="pageNumber"
          :isActive="activePage"
          @onClicked="setActivePage"
        />
        <PaginationEllipsis />
        <PaginationElement
          v-for="pageNumber in [attachmentsPage.totalPages - 1, attachmentsPage.totalPages]"
          :key="pageNumber"
          :page="pageNumber"
          :isActive="activePage"
          @onClicked="setActivePage"
        />
      </div>
      <PaginationNext :disabled="lastPage" @onClicked="handleClickNext" />
    </div>
  </div>
</template>
<script lang="ts" setup>
import PaginationBack from '../atoms/PaginationBack.vue'
import PaginationElement from '@/design-system/atoms/PaginationElement.vue'
import PaginationEllipsis from '@/design-system/atoms/PaginationEllipsis.vue'
import PaginationNext from '../atoms/PaginationNext.vue'
import { Attachment, AttachmentsPage } from '@/services/attachments.service'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

// emits
const emits = defineEmits<{
  (event: 'next'): void
  (event: 'back'): void
  (event: 'page', value: number): void
}>()

// props
const props = defineProps<{
  attachmentsPage?: AttachmentsPage<Attachment>
}>()
// data
const { t } = useI18n()
const activePage = ref<number>(1)
const lastPage = computed<boolean>(() => props.attachmentsPage?.totalPages === activePage.value)

const lastPages = computed<number[]>(() => {
  if (!props.attachmentsPage?.totalPages) return [0]
  return [
    props.attachmentsPage?.totalPages - 4,
    props.attachmentsPage?.totalPages - 3,
    props.attachmentsPage?.totalPages - 2,
    props.attachmentsPage?.totalPages - 1,
    props.attachmentsPage?.totalPages
  ]
})

// methods
function handleClickBack() {
  if (activePage.value > 1) {
    activePage.value--
    emits('back')
  }
}
function handleClickNext() {
  if (!lastPage.value) {
    activePage.value++
    emits('next')
  }
}
function setActivePage(value: number) {
  activePage.value = value
  emits('page', value - 1) // -1 because the attachementsPage is 0 based
}
watch(
  () => props.attachmentsPage,
  (newVal) => {
    if (!newVal) return
    activePage.value = newVal.number + 1
  }
)
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';

.page-group {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 8px 0 12px;
  .items {
    display: flex;
    align-items: center;
    justify-content: center;
    @include font-mixins.desktop-regular-paragraph;
    color: variables.$color-greyscale-content;
  }
  .pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
  }
}
</style>
